import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import { deleteFarmSubdivision } from '../../services/farmMapping/deleteFarmSubdivision'
import getFarmSubdivisions from '../../services/farmMapping/getFarmSubdivision'
import { createFarmSubdivision } from '../../services/farmMapping/saveFarmSubdivision'
import { updateFarmSubdivision } from '../../services/farmMapping/updateFarmSubdivision'
import { uploadFarmSubdivisionTemplate } from '../../services/farmMapping/uploadFarmSubdivisionTemplate'
import { farmReadsExceptPerimeterDerived, queryKeys } from './queryKeys'

// Keyed on the calendar year: resolving a single loteo by its own id is getFarmSubdivisionById,
// not a narrowing of this read.
export const farmSubdivisionsQueryOptions = (farmId: string | undefined, year?: string | number) =>
  queryOptions({
    queryKey: queryKeys.farmSubdivisions(farmId, year),
    queryFn: ({ signal }) => getFarmSubdivisions({ farmId: farmId as string, year, signal }),
  })

export const useFarmSubdivisions = ({
  farmId,
  year,
  enabled = true,
}: {
  farmId: string | undefined
  year?: string | number
  enabled?: boolean
}) =>
  useQuery({
    ...farmSubdivisionsQueryOptions(farmId, year),
    enabled: enabled && !!farmId,
  })

// These writes run inside the caller's processLoading wrapper, which already surfaces failures
// through the loading modal — letting the global handler fire too would double-report.
const suppressGlobalError = { suppressGlobalError: true }

// A loteo write changes which paddocks the QU derivation runs over, so the QU reads go with it.
// Unconditional rather than gated on quConfig.enabled: a farm that never reads them has nothing
// cached under these keys.
const invalidateSubdivisionReads = (queryClient: QueryClient, farmId: string | undefined) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.farmSubdivisionsRoot(farmId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.quantificationUnitsRoot(farmId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.quHintsRoot(farmId) })
  // The project area only unions the in-project paddocks listed in the LP-year loteo.
  queryClient.invalidateQueries({ queryKey: queryKeys.projectArea(farmId) })
}

// Defaults off: only a caller that rebuilds these roots itself, after its whole write sequence
// settles, may opt out.
type InvalidationOptions = { skipInvalidation?: boolean }

export const useCreateFarmSubdivision = ({
  skipInvalidation = false,
}: InvalidationOptions = {}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { farmSubdivision: Record<string, unknown> }) =>
      createFarmSubdivision(input),
    meta: suppressGlobalError,
    onSuccess: (created: { farmId: string }) => {
      if (skipInvalidation) return
      invalidateSubdivisionReads(queryClient, created?.farmId)
    },
  })
}

export const useUpdateFarmSubdivision = ({
  skipInvalidation = false,
}: InvalidationOptions = {}) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: {
      farmSubdivision: { id: string; farmId?: string } & Record<string, unknown>
    }) => updateFarmSubdivision(input),
    meta: suppressGlobalError,
    onSuccess: (_result, variables) => {
      if (skipInvalidation) return
      invalidateSubdivisionReads(queryClient, variables.farmSubdivision?.farmId)
    },
  })
}

// farmId is carried in the variables purely to key the invalidation: the DELETE URL identifies the
// loteo alone.
export const useDeleteFarmSubdivision = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      id,
      deletePaddocks,
    }: {
      id: string
      deletePaddocks?: boolean
      farmId: string | undefined
    }) => deleteFarmSubdivision({ id, deletePaddocks }),
    meta: suppressGlobalError,
    onSuccess: (_result, variables) => {
      // deletePaddocks=true cascades into the paddocks, the loteo and (transitively) the QUs and
      // metric events, so the farm subtree is the honest blast radius — but it reaches neither of
      // the two roots below.
      queryClient.invalidateQueries(farmReadsExceptPerimeterDerived(variables.farmId))
      queryClient.invalidateQueries({ queryKey: queryKeys.paddocksByIdsRoot })
      queryClient.invalidateQueries({ queryKey: queryKeys.metricEventsRoot })
    },
  })
}

export const useUploadSubdivisionTemplate = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: { file: File; farmId: string }) => uploadFarmSubdivisionTemplate(input),
    meta: suppressGlobalError,
    // templateFileName lives on the subdivision row, so the loteo list is what goes stale.
    onSuccess: (_result, variables) => invalidateSubdivisionReads(queryClient, variables.farmId),
  })
}
